import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  generate856,
  get856Preview,
  get856Workflow,
  prepare856Submission,
  send856Submission,
} from "../utils/ediApi";

import PoDifferenceReview, { hasPoReviewContent } from "../components/PoDifferenceReview";

export const BATCH_SELECTABLE_STATES = new Set(["READY_TO_GENERATE", "READY_TO_REVIEW", "READY_TO_SEND"]);
export const BATCH_POLL_STATES = new Set(["SUBMITTING", "AWAITING_MDN", "AWAITING_997"]);
const SAFE_AFTER_SEND = new Set(["AWAITING_MDN", "AWAITING_997", "COMPLETED"]);
const EXISTING_TRANSMISSION_STATES = new Set([
  "SUBMITTING", "AWAITING_MDN", "AWAITING_997", "COMPLETED", "WALMART_REJECTED", "IT_ATTENTION_REQUIRED",
]);

const shown = (value) => value === null || value === undefined || value === "" ? "—" : String(value);
const titleIssue = (value) => String(value || "Unknown blocker").toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function batchStateLabel(state) {
  return {
    READY_TO_GENERATE: "Ready to Generate",
    READY_TO_REVIEW: "Ready to Review",
    READY_TO_SEND: "Ready to Send",
    SUBMITTING: "Submitting",
    AWAITING_MDN: "Waiting for AS2 Confirmation",
    AWAITING_997: "Waiting for Walmart 997",
    COMPLETED: "Accepted",
    HISTORICAL_856_EXISTS: "Already Submitted",
    PREVIEW_BLOCKED: "Blocked",
    BUSINESS_CONTEXT_BLOCKED: "Shipment or PO data changed",
    WALMART_REJECTED: "Walmart Rejected",
    IT_ATTENTION_REQUIRED: "IT Attention Required",
  }[state] || shown(state);
}

function sumShippedUnits(items) {
  const values = (items || []).map((item) => displayNumber(item.shippedQuantity));
  return values.length && values.every((value) => value !== null) ? values.reduce((sum, value) => sum + value, 0) : null;
}

function displayNumber(value) {
  if (value === null || value === undefined || typeof value === "boolean"
    || !["number", "string"].includes(typeof value) || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function candidateView(candidate) {
  const shippedUnits = sumShippedUnits(candidate?.items);
  const display = candidate?.displayShipment?.authoritative === false
    && candidate?.displayShipment?.source === "ROUTING_STORED_WMS_PAYLOAD" ? candidate.displayShipment : null;
  const projectedCount = (value) => {
    const count = displayNumber(value);
    return count !== null && Number.isInteger(count) ? count : null;
  };
  const authoritativeCount = (value) => {
    const count = projectedCount(value);
    return shippedUnits !== null && (count > 0 || (count === 0 && shippedUnits === 0)) ? count : null;
  };
  const informationalUnits = displayNumber(display?.shippedUnits);
  const informationalCount = (value) => {
    const count = projectedCount(value);
    return informationalUnits !== null && (count > 0 || (count === 0 && informationalUnits === 0)) ? count : null;
  };
  return {
    poNumber: String(candidate?.poNumber || ""),
    shipTo: candidate?.shipTo?.name,
    shipToGln: candidate?.shipTo?.gln,
    shipDate: candidate?.shipment?.shippedDate,
    weight: candidate?.shipment?.weight,
    loadingMethod: candidate?.handling?.loadingMethod,
    shippedUnits: shippedUnits ?? informationalUnits,
    packCount: authoritativeCount(candidate?.handling?.plannedPackCount) ?? informationalCount(display?.projectedPackCount),
    ssccCount: authoritativeCount(candidate?.sscc?.requiredCount) ?? informationalCount(display?.projectedSsccCount),
    informational: shippedUnits === null && informationalUnits !== null,
    previewIssues: candidate?.blockingIssues || [],
    poComparison: candidate?.poComparison,
  };
}

function BatchReviewModal({ rows, busy, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  const [approved,setApproved] = useState(false);
  useEffect(() => { setApproved(false); }, [rows]);
  const needsApproval = rows.some(x=>x.submission.poComparison?.confirmableDifferences?.length);
  const hardBlocked = rows.some(x=>x.submission.poComparison?.hardBlockers?.length);
  const totals = rows.reduce((value, row) => ({
    packs: value.packs + (Number(row.submission.packCount) || 0),
    ssccs: value.ssccs + (Number(row.submission.ssccCount) || 0),
  }), { packs: 0, ssccs: 0 });
  useEffect(() => { cancelRef.current?.focus(); }, []);
  useEffect(() => {
    const key = (event) => { if (event.key === "Escape" && !busy) onCancel(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [busy, onCancel]);
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
    <section role="dialog" aria-modal="true" aria-labelledby="batch-856-review-title" className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
      <h2 id="batch-856-review-title" className="text-xl font-bold">Review Selected 856s Before Sending</h2>
      <p className="mt-2 text-sm text-slate-600">Review the exact server-prepared documents below. Nothing has been sent yet.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead><tr className="border-b bg-slate-50"><th className="p-2">PO Number</th><th className="p-2">Ship To</th><th className="p-2">Ship-To GLN</th><th className="p-2">Ship Date</th><th className="p-2">Weight</th><th className="p-2">Pack Count</th><th className="p-2">SSCC Count</th><th className="p-2">ISA13</th><th className="p-2">GS06</th><th className="p-2">ST02</th></tr></thead>
          <tbody>{rows.map(({ poNumber, submission }) => <tr key={poNumber} className="border-b"><td className="p-2 font-medium">{poNumber}</td><td className="p-2">{shown(submission.shipToName)}</td><td className="p-2">{shown(submission.shipToGln)}</td><td className="p-2">{shown(submission.shipDate)}</td><td className="p-2">{shown(submission.weight)}</td><td className="p-2">{shown(submission.packCount)}</td><td className="p-2">{shown(submission.ssccCount)}</td><td className="p-2">{shown(submission.controlNumbers?.isa13)}</td><td className="p-2">{shown(submission.controlNumbers?.gs06)}</td><td className="p-2">{shown(submission.controlNumbers?.st02)}</td></tr>)}</tbody>
          <tfoot><tr className="font-semibold"><td className="p-2" colSpan="5">PO Count: {rows.length}</td><td className="p-2">{totals.packs}</td><td className="p-2">{totals.ssccs}</td><td colSpan="3"/></tr></tfoot>
        </table>
      </div>
      {rows.filter(row=>hasPoReviewContent(row.submission.poComparison)).map(row=><PoDifferenceReview key={row.poNumber} poNumber={row.poNumber} comparison={row.submission.poComparison}/>)}
      {needsApproval && !hardBlocked && <label className="block text-sm"><input type="checkbox" disabled={busy} checked={approved} onChange={e=>setApproved(e.target.checked)}/> I have reviewed all listed differences and approve sending these 856s.</label>}
      <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
        <p>This will submit the selected 856 documents through the production AS2 gateway.</p>
        <p className="mt-1 font-semibold">Each document will be sent once.</p>
        <p className="mt-1 font-semibold">Do not retry any document with an unknown transmission outcome.</p>
      </div>
      <div className="mt-5 flex justify-end gap-3"><button ref={cancelRef} type="button" disabled={busy} onClick={onCancel} className="rounded border px-4 py-2 disabled:opacity-50">Cancel</button><button type="button" disabled={busy || hardBlocked || (needsApproval && !approved)} onClick={()=>onConfirm(approved)} className="rounded bg-red-700 px-4 py-2 text-white disabled:opacity-50">{busy ? "Working..." : `Send ${rows.length} 856s to Walmart`}</button></div>
    </section>
  </div>;
}

function StatusTable({ rows }) {
  if (!rows.length) return null;
  return <section className="rounded-lg border bg-white p-4"><h2 className="text-lg font-semibold">Batch Status</h2><div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-sm"><thead><tr className="border-b bg-slate-50"><th className="p-2">PO Number</th><th className="p-2">Generation</th><th className="p-2">ISA13</th><th className="p-2">ST02</th><th className="p-2">Transport</th><th className="p-2">Walmart 997</th><th className="p-2">Final Status</th></tr></thead><tbody>{rows.map((row) => {
    const state = row.workflow?.workflowState;
    const walmart997 = state === "COMPLETED" ? "Accepted" : state === "WALMART_REJECTED" ? "Rejected" : state === "AWAITING_997" ? "Waiting" : "—";
    return <tr key={row.poNumber} className="border-b"><td className="p-2 font-medium">{row.poNumber}</td><td className="p-2">{shown(row.workflow?.generation?.generationId)}</td><td className="p-2">{shown(row.workflow?.artifact?.controls?.isa13)}</td><td className="p-2">{shown(row.workflow?.artifact?.controls?.st02)}</td><td className="p-2">{shown(row.workflow?.submission?.transportState || state)}</td><td className="p-2">{walmart997}</td><td className="p-2">{batchStateLabel(state)}</td></tr>;
  })}</tbody></table></div></section>;
}

export default function Walmart856Batch({ full, headers, onPermissionDenied, onMutationChange, initialLoadId = "" }) {
  const [loadId, setLoadId] = useState(String(initialLoadId || ""));
  const [activeLoadId, setActiveLoadId] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [runPoNumbers, setRunPoNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [reviewRows, setReviewRows] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [error, setError] = useState("");
  const [halted, setHalted] = useState(false);
  const mounted = useRef(true);
  const requestId = useRef(0);
  const mutationActive = useRef(false);
  const operationGeneration = useRef(0);
  const pollActive = useRef(false);
  const fullRef = useRef(full);
  const previousFull = useRef(full);
  const lastInitialLoadId = useRef("");
  const mutationChangeRef = useRef(onMutationChange);
  fullRef.current = full;
  mutationChangeRef.current = onMutationChange;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current += 1;
      operationGeneration.current += 1;
      mutationActive.current = false;
    };
  }, []);
  useEffect(() => {
    if (previousFull.current && !full) {
      operationGeneration.current += 1;
      mutationActive.current = false;
      setReviewOpen(false); setReviewRows([]); setSelected([]);
      setBusyAction(null);
      setError("Your Walmart 856 permission changed. The batch is now read-only.");
      mutationChangeRef.current?.(false);
    }
    previousFull.current = full;
  }, [full]);

  const beginMutation = (action) => {
    const operation = ++operationGeneration.current;
    mutationActive.current = true;
    setBusyAction(action);
    mutationChangeRef.current?.(true);
    return operation;
  };

  const operationIsActive = (operation) => (
    mounted.current
    && mutationActive.current
    && operationGeneration.current === operation
    && fullRef.current
  );

  const finishMutation = (operation) => {
    if (operationGeneration.current !== operation) return;
    mutationActive.current = false;
    if (mounted.current) {
      setBusyAction(null);
      mutationChangeRef.current?.(false);
    }
  };

  const updateWorkflow = useCallback((poNumber, workflow) => {
    if (!mounted.current) return;
    setRows((current) => current.map((row) => row.poNumber === poNumber ? { ...row, workflow } : row));
  }, []);

  const loadBatch = useCallback(async (requestedLoadId, { preserveSelection = false } = {}) => {
    const normalized = String(requestedLoadId || "").trim();
    if (!normalized) return false;
    const id = ++requestId.current;
    setLoading(true); setError(""); setReviewOpen(false); setReviewRows([]); setStatusVisible(false); setHalted(false);
    try {
      const preview = await get856Preview(normalized, { headers });
      const candidates = Array.isArray(preview.asnCandidates) ? preview.asnCandidates : [];
      const workflows = await Promise.all(candidates.map((candidate) => get856Workflow(normalized, candidate.poNumber, { headers })));
      if (!mounted.current || id !== requestId.current) return false;
      const nextRows = candidates.map((candidate, index) => ({ ...candidateView(candidate), workflow: workflows[index] }));
      setRows(nextRows); setActiveLoadId(normalized); setRunPoNumbers([]);
      setSelected((current) => preserveSelection ? current.filter((po) => nextRows.some((row) => row.poNumber === po && BATCH_SELECTABLE_STATES.has(row.workflow?.workflowState))) : []);
      return true;
    } catch (e) {
      if (mounted.current && id === requestId.current) setError(e.message || "Unable to load the Walmart 856 batch.");
      return false;
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    const normalized = String(initialLoadId || "").trim();
    if (!normalized || lastInitialLoadId.current === normalized) return;
    lastInitialLoadId.current = normalized;
    setLoadId(normalized);
    loadBatch(normalized);
  }, [initialLoadId, loadBatch]);

  const permissionFailure = useCallback(async (e) => {
    if (e?.status !== 403 && e?.errorCode !== "FORBIDDEN") return false;
    fullRef.current = false;
    operationGeneration.current += 1;
    mutationActive.current = false;
    if (mounted.current) {
      setReviewOpen(false); setReviewRows([]); setSelected([]); setBusyAction(null);
      setError("Your Walmart 856 permission changed. The batch is now read-only.");
      mutationChangeRef.current?.(false);
      await Promise.resolve(onPermissionDenied?.()).catch(() => {});
    }
    return true;
  }, [onPermissionDenied]);

  const toggle = (poNumber) => setSelected((current) => current.includes(poNumber) ? current.filter((value) => value !== poNumber) : [...current, poNumber]);
  const selectable = useMemo(() => rows.filter((row) => BATCH_SELECTABLE_STATES.has(row.workflow?.workflowState)).map((row) => row.poNumber), [rows]);
  const allSelected = selectable.length > 0 && selectable.every((po) => selected.includes(po));

  const generateAndReview = async () => {
    if (mutationActive.current || !fullRef.current || !activeLoadId) return;
    const targets = rows.filter((row) => selected.includes(row.poNumber));
    if (!targets.length) { setError("Select at least one eligible PO."); return; }
    const operation = beginMutation("prepare");
    setError(""); setHalted(false); setStatusVisible(false); setRunPoNumbers(targets.map((row) => row.poNumber));
    const ready = [];
    let failure = null;
    let activePoNumber = targets[0].poNumber;
    try {
      for (const row of targets) {
        activePoNumber = row.poNumber;
        if (!operationIsActive(operation)) return;
        let workflow = await get856Workflow(activeLoadId, row.poNumber, { headers });
        if (!operationIsActive(operation)) return;
        updateWorkflow(row.poNumber, workflow);
        if (workflow.workflowState === "READY_TO_GENERATE") {
          await generate856(activeLoadId, row.poNumber, { headers });
          if (!operationIsActive(operation)) return;
          workflow = await get856Workflow(activeLoadId, row.poNumber, { headers });
          if (!operationIsActive(operation)) return;
          updateWorkflow(row.poNumber, workflow);
        }
        if (["COMPLETED", "HISTORICAL_856_EXISTS"].includes(workflow.workflowState)) continue;
        if (!["READY_TO_REVIEW", "READY_TO_SEND"].includes(workflow.workflowState) || !workflow.generation?.generationId) {
          failure = `${row.poNumber}: This PO is no longer ready to generate.`;
          break;
        }
        ready.push({ poNumber: row.poNumber, generationId: workflow.generation.generationId });
      }
      if (failure) { setError(failure); return; }
      if (!ready.length) { setError("No selected PO still requires review or sending."); return; }
      const prepared = [];
      for (const row of ready) {
        activePoNumber = row.poNumber;
        if (!operationIsActive(operation)) return;
        const value = await prepare856Submission(row.generationId, { headers });
        if (!operationIsActive(operation)) return;
        if (!value?.submission?.confirmationToken) throw new Error("Some shipment data changed. Refresh the batch before continuing.");
        prepared.push({ ...row, submission: value.submission });
      }
      if (mounted.current) { setReviewRows(prepared); setReviewOpen(true); }
    } catch (e) {
      const message = `${activePoNumber}: ${e?.message || "Some shipment data changed. Refresh the batch before continuing."}`;
      const denied = await permissionFailure(e);
      if (!operationIsActive(operation)) return;
      setReviewRows([]); setReviewOpen(false);
      if (!denied) {
        await loadBatch(activeLoadId, { preserveSelection: true });
        if (mounted.current) setError(message);
      }
    } finally {
      finishMutation(operation);
    }
  };

  const stopBatch = (poNumber, message) => {
    if (!mounted.current) return;
    setHalted(true);
    setError(`${poNumber}: Batch stopped for safety. ${message}`);
  };

  const sendSelected = async (differencesApproved = false) => {
    if (mutationActive.current || !fullRef.current || !reviewRows.length) return;
    const targets = [...reviewRows];
    if (targets.some(x=>x.submission.poComparison?.hardBlockers?.length || (x.submission.poComparison?.confirmableDifferences?.length && !differencesApproved))) return;
    const operation = beginMutation("send");
    setError(""); setHalted(false); setStatusVisible(true); setReviewOpen(false); setReviewRows([]);
    try {
      for (const row of targets) {
        if (!operationIsActive(operation)) return;
        let workflow = await get856Workflow(activeLoadId, row.poNumber, { headers });
        if (!operationIsActive(operation)) return;
        updateWorkflow(row.poNumber, workflow);
        if (EXISTING_TRANSMISSION_STATES.has(workflow.workflowState)) {
          if (SAFE_AFTER_SEND.has(workflow.workflowState)) continue;
          const message = workflow.workflowState === "SUBMITTING"
            ? "This PO is still submitting. No remaining documents were sent."
            : "An existing transmission requires review. No remaining document was sent.";
          stopBatch(row.poNumber, message);
          break;
        }
        if (!["READY_TO_REVIEW", "READY_TO_SEND"].includes(workflow.workflowState)
            || workflow.generation?.generationId !== row.generationId) {
          stopBatch(row.poNumber, "Some shipment data changed. Refresh the batch before continuing.");
          break;
        }
        let result;
        try {
          result = await send856Submission(row.generationId, row.submission.confirmationToken, { headers, ...(row.submission.poComparison?.confirmableDifferences?.length ? { differencesConfirmed: differencesApproved } : {}) });
        } catch (e) {
          if (!operationIsActive(operation)) return;
          try {
            workflow = await get856Workflow(activeLoadId, row.poNumber, { headers });
            if (!operationIsActive(operation)) return;
            updateWorkflow(row.poNumber, workflow);
          } catch { /* preserve the original send classification */ }
          if (await permissionFailure(e)) break;
          const copy = e?.errorCode === "TRANSPORT_OUTCOME_UNKNOWN"
            ? "The transmission outcome is unknown. Do not resend this document."
            : e?.message || "The transmission requires IT attention. Do not resend this document.";
          stopBatch(row.poNumber, copy);
          break;
        }
        if (!operationIsActive(operation)) return;
        const submission = result?.submission || {};
        if (submission.state === "SUBMITTING") {
          updateWorkflow(row.poNumber, { ...workflow, workflowState: "SUBMITTING", submission });
          stopBatch(row.poNumber, "This PO is still submitting. No remaining documents were sent.");
          break;
        }
        if (!SAFE_AFTER_SEND.has(submission.state) && submission.transportState !== "MDN_ACCEPTED") {
          stopBatch(row.poNumber, "The transmission requires IT attention. Do not resend this document.");
          break;
        }
        workflow = await get856Workflow(activeLoadId, row.poNumber, { headers });
        if (!operationIsActive(operation)) return;
        updateWorkflow(row.poNumber, workflow);
        if (workflow.workflowState === "SUBMITTING") {
          stopBatch(row.poNumber, "This PO is still submitting. No remaining documents were sent.");
          break;
        }
        if (!SAFE_AFTER_SEND.has(workflow.workflowState)) {
          stopBatch(row.poNumber, "The transmission requires IT attention. No remaining document was sent.");
          break;
        }
      }
    } catch (e) {
      if (!(await permissionFailure(e))) stopBatch(e?.poNumber || targets[0].poNumber, e?.message || "No remaining document was sent.");
    } finally {
      if (mounted.current) setSelected([]);
      finishMutation(operation);
    }
  };

  const runRows = useMemo(() => runPoNumbers.map((po) => rows.find((row) => row.poNumber === po)).filter(Boolean), [rows, runPoNumbers]);
  const pendingKey = runRows.filter((row) => BATCH_POLL_STATES.has(row.workflow?.workflowState)).map((row) => row.poNumber).join("|");
  useEffect(() => {
    if (!pendingKey) return undefined;
    const poll = async () => {
      if (pollActive.current || mutationActive.current) return;
      pollActive.current = true;
      try {
        const pending = pendingKey.split("|");
        const values = await Promise.all(pending.map((po) => get856Workflow(activeLoadId, po, { headers })));
        values.forEach((workflow, index) => updateWorkflow(pending[index], workflow));
      } catch (e) { setError(e.message || "Unable to refresh the batch status."); }
      finally { pollActive.current = false; }
    };
    const timer = window.setInterval(poll, 5000);
    return () => window.clearInterval(timer);
  }, [activeLoadId, headers, pendingKey, updateWorkflow]);

  const summary = useMemo(() => runRows.reduce((value, row) => {
    const state = row.workflow?.workflowState;
    if (state === "COMPLETED") value.accepted += 1;
    else if (BATCH_POLL_STATES.has(state)) value.waiting += 1;
    else if (state === "WALMART_REJECTED") value.rejected += 1;
    else if (state === "IT_ATTENTION_REQUIRED") value.attention += 1;
    return value;
  }, { accepted: 0, waiting: 0, rejected: 0, attention: 0 }), [runRows]);

  return <section className="space-y-5" aria-labelledby="batch-856-title">
    <header><h2 id="batch-856-title" className="text-xl font-semibold">Batch 856</h2><p className="mt-1 text-sm text-slate-600">Select eligible POs once, review all prepared documents together, then authorize one sequential send batch.</p></header>
    <form className="grid gap-3 rounded-lg border bg-white p-4 sm:grid-cols-[1fr_auto]" onSubmit={(event) => { event.preventDefault(); if (!busyAction) loadBatch(loadId); }}><label className="text-sm font-medium">Load ID<input aria-label="Batch Load ID" disabled={Boolean(busyAction)} value={loadId} onChange={(event) => setLoadId(event.target.value)} className="mt-1 w-full rounded border px-3 py-2 disabled:bg-slate-100"/></label><div className="flex items-end"><button type="submit" disabled={Boolean(busyAction) || loading || !loadId.trim()} className="w-full rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{loading ? "Looking up..." : "Lookup Load"}</button></div></form>
    {error && <div role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</div>}
    {rows.some((row) => row.informational) && <p className="text-sm text-slate-600">Blocked-row quantities are informational values from stored WMS data. Pack and SSCC counts for these shipments are projected requirements.</p>}
    {!full && rows.length > 0 && <div className="rounded bg-slate-50 p-3 text-sm">Your Walmart 856 permission is read-only. You can view this batch but cannot generate or send documents.</div>}
    {rows.length > 0 && <section className="rounded-lg border bg-white p-4"><div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-semibold">Load {activeLoadId}</h2>{full && <button type="button" disabled={Boolean(busyAction) || !selected.length} onClick={generateAndReview} className="rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{busyAction === "prepare" ? "Preparing batch..." : "Generate & Review Selected"}</button>}</div><div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-sm"><thead><tr className="border-b bg-slate-50"><th className="p-2"><label className="flex items-center gap-2"><input aria-label="Select all eligible POs" type="checkbox" disabled={!full || !selectable.length || Boolean(busyAction)} checked={allSelected} onChange={() => setSelected(allSelected ? [] : selectable)}/>Select</label></th><th className="p-2">PO Number</th><th className="p-2">Ship To</th><th className="p-2">Ship Date</th><th className="p-2">Loading Method</th><th className="p-2">Shipped Units</th><th className="p-2">Pack Count</th><th className="p-2">SSCC Count</th><th className="p-2">Status</th></tr></thead><tbody>{rows.map((row) => {
      const state = row.workflow?.workflowState;
      const eligible = BATCH_SELECTABLE_STATES.has(state);
      const comparison = row.workflow?.submission?.poComparison || row.workflow?.preview?.poComparison || row.poComparison;
      const issues = row.workflow?.submission?.poComparison?.hardBlockers?.map(x=>x.code) || row.workflow?.preview?.blockingIssues || row.previewIssues;
      return <tr key={row.poNumber} className="border-b"><td className="p-2"><input aria-label={`Select PO ${row.poNumber}`} type="checkbox" disabled={!full || !eligible || Boolean(busyAction)} checked={selected.includes(row.poNumber)} onChange={() => toggle(row.poNumber)}/></td><td className="p-2 font-medium">{row.poNumber}</td><td className="p-2">{shown(row.shipTo)}</td><td className="p-2">{shown(row.shipDate)}</td><td className="p-2">{shown(row.loadingMethod)}</td><td className="p-2">{shown(row.shippedUnits)}</td><td className="p-2">{shown(row.packCount)}</td><td className="p-2">{shown(row.ssccCount)}</td><td className="p-2"><span>{batchStateLabel(state)}</span>{["PREVIEW_BLOCKED","BUSINESS_CONTEXT_BLOCKED"].includes(state) && issues?.length > 0 && <div className="mt-1 text-xs text-amber-800">{issues.map(titleIssue).join(", ")}</div>}{hasPoReviewContent(comparison) && <details><summary>PO comparison</summary><PoDifferenceReview poNumber={row.poNumber} comparison={comparison}/></details>}</td></tr>;
    })}</tbody></table></div></section>}
    {statusVisible && runRows.length > 0 && <div className={`rounded p-4 ${halted ? "bg-red-50 text-red-900" : summary.waiting ? "bg-amber-50 text-amber-900" : "bg-emerald-50 text-emerald-900"}`}><h2 className="font-semibold">{halted ? "Batch Stopped for Safety" : summary.waiting ? "Batch In Progress" : "Batch Complete"}</h2><p className="mt-1 text-sm">Accepted: {summary.accepted} · Waiting: {summary.waiting} · Rejected: {summary.rejected} · IT Attention: {summary.attention}</p>{halted && <p className="mt-2 text-sm">The batch stopped before sending the remaining documents because one transmission requires IT attention.</p>}</div>}
    {statusVisible && <StatusTable rows={runRows}/>}
    {full && reviewOpen && reviewRows.length > 0 && <BatchReviewModal rows={reviewRows} busy={busyAction === "send"} onCancel={() => { if (!mutationActive.current) { setReviewOpen(false); setReviewRows([]); } }} onConfirm={sendSelected}/>}
  </section>;
}
